import { Table } from "flowbite-react";
import { ICreateExpenseData, ItemInput } from "../../../../@types";
import LineItem from "./LineItem";
import React, { useEffect, useState } from "react";
import { LineItemMobile } from "./LineItemMobile";
// @ts-ignore
import AddIcon from "../../../../assets/icons/AddIcon";
import { useTranslation } from "react-i18next";
import { ModuleName } from "../../../../types/subscription";
import { useSubscription } from "../../../../context/SubscriptionProvider";

export interface TableColumn {
  toLowerCase(): string;
  label: string;
  key: keyof ItemInput;
}

export interface Props {
  columns: TableColumn[];
  items: ItemInput[];
  isSearchInput: boolean;
  addItem: () => void;
  removeItem: (index: any) => void;
  updateCell: (index: number, field: string, value: string | number) => void;
  updateRow: (index: number, item: ItemInput, quantity?: number) => void;
  isImportedToInvoice?: boolean;
  onUpdateVariation?: (id: number, field: string, value: string) => void;
  isInvoice: boolean;
  handleClearErrors: () => void;
  currency: string;
  exchangeRate: number;
  handleAdd?: () => void;
  isSidebarCollapsed?: boolean; // Add this prop
  expenseValidationResults?: ICreateExpenseData;
}

const LineItemTable: React.FC<Props> = ({
  columns,
  items,
  isSearchInput = false,
  addItem,
  removeItem,
  updateCell,
  updateRow,
  isImportedToInvoice,
  onUpdateVariation,
  isInvoice = false,
  handleClearErrors,
  currency,
  exchangeRate,
  handleAdd,
  isSidebarCollapsed,
  expenseValidationResults,
}) => {
  const { t } = useTranslation("invoices");
  const [errors, setErrors] = React.useState<string[][]>(items.map(() => []));
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { SubscriptionGuard } = useSubscription();
  const [isMobileTableExpanded, setIsMobileTableExpanded] = useState(true);
  const [isMiniCardVisible, setIsMiniCardVisible] = useState(false);

  useEffect(() => {
    const observer = new MutationObserver((mutationsList, observer) => {
      for (let mutation of mutationsList) {
        if (mutation.type === "childList") {
          const tableElement = document.querySelector(
            '[data-testid="table-element"]',
          ) as HTMLElement;
          if (tableElement) {
            tableElement.style.overflowX = "visible";
            observer.disconnect();
          }
        }
      }
    });

    observer.observe(document, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const clearErrorsForIndex = (index: number) => {
    setErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      newErrors[index] = [];
      return newErrors;
    });
  };

  const updateRowAndValidate = (index: number, item: ItemInput) => {
    updateRow(index, item, 1);

    setErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      const fields: string[] = [];

      if (!item.name) fields.push("name");
      if (!item.quantity || item.quantity < 0) fields.push("quantity");
      if (!item.rate || item.rate < 0) fields.push("rate");

      // If there are no errors, clear them for this row
      newErrors[index] = fields.length > 0 ? fields : [];

      return newErrors;
    });
  };

  // Function to update error state
  const updateError = (index: number, error: string[]) => {
    setErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      newErrors[index] = error;
      return newErrors;
    });
  };

  const validateItems = (items: ItemInput[]) => {
    const newErrors = items.map((item) => {
      const fields: string[] = [];
      if (!item.name) fields.push("name");
      if (!item.quantity || item.quantity < 0) fields.push("quantity");
      if (!item.rate || item.rate < 0) fields.push("rate");
      return fields;
    });

    setErrors(newErrors);

    const isValid = items.every((item) => {
      return !(
        !item?.name ||
        !item?.quantity ||
        item.quantity < 0 ||
        !item?.rate ||
        item.rate < 0
      );
    });

    return isValid;
  };

  // const [expandedRowIndex, setExpandedRowIndex] = useState<number>(-1);
  // const [isExpanded, setIsExpanded] = useState(false);
  // const [expandedTableIndex, setExpandedTableIndex] = useState<number | null>(
  //   0
  // );

  const handleAddItem = () => {
    if (validateItems(items)) {
      setErrors((prevErrors) => [...prevErrors, []]); // Add empty error state for new row
      addItem();
    } else {
    }
  };

  const renderErrors = (index: number) => {
    return errors[index]?.length > 0 ? (
      <ul className="text-red-500 text-sm">
        {errors[index].map((error, i) => (
          <li key={i}>{error}</li>
        ))}
      </ul>
    ) : null;
  };

  const getExistingVariationTypes = (item: any) => {
    // Find all instances of the item in the list
    const existingItems = items.filter((i) => i.id === item.id && i.variation);

    // Extract all selected variation types
    const selectedVariationTypes = existingItems
      // @ts-ignore
      .map((i) => i.variation?.type)
      .filter(Boolean);

    return selectedVariationTypes; // Returns an array of already selected variations
  };

  return (
    <>
      <div className=" px-0 ">
        <Table
          className={` px-0 ${isSidebarCollapsed ? "sidebar-collapsed" : "sidebar-expanded"}`}
        >
          {!isMobile && (
            <Table.Head className="text-[16px] font-medium">
              {columns.map((column, index) => (
                <Table.HeadCell className="font-medium capitalize" key={index}>
                  {column.label}
                </Table.HeadCell>
              ))}
            </Table.Head>
          )}
          <Table.Body className="divide-y">
            {items.map((item, index) => (
              <React.Fragment key={`${item.id}-${index}`}>
                <LineItem
                  key={`${item.id}-${index}`}
                  index={index}
                  columns={columns}
                  isSearchInput={isSearchInput}
                  item={items[index]}
                  removeItem={removeItem}
                  updateCell={updateCell}
                  updateRow={updateRowAndValidate}
                  errors={errors[index] || []}
                  isSidebarCollapsed={isSidebarCollapsed} // Pass the prop to LineItem
                  updateError={(newErrors) => {
                    setErrors((prev) => {
                      const updatedErrors = [...prev];
                      return updatedErrors;
                    });
                  }}
                  isInvoice={isInvoice}
                  handleClearErrors={handleClearErrors}
                  currency={currency}
                  exchangeRate={exchangeRate}
                  setErrors={setErrors}
                  getExistingVariationTypes={getExistingVariationTypes}
                />
                {/* {renderErrors(index)} */}
              </React.Fragment>
            ))}
          </Table.Body>
        </Table>
        <div className="flex justify-between items-center  mt-6">
          {expenseValidationResults && (
            <span
              className="text-red-500 ml-[2em] error-message"
              style={{ whiteSpace: "nowrap" }}
            >
              {expenseValidationResults.lineItems}
            </span>
          )}
          {isInvoice && (
            <SubscriptionGuard
              createTitle="Add New Inventory"
              outlined={true}
              module={"inventories" as ModuleName}
              isListPage
            >
              <div>
                {isInvoice && (
                  <div className=" absolute hidden lg:block md:block xl:mt-[-1em] biglg:mt-[-1em] lg:mt-[-1em]">
                    <span
                      onClick={handleAdd}
                      className="flex items-center text-[#044E97] font-medium cursor-pointer w-[180px] py-2 px-[1em] rounded-md ml-[2em]"
                    >
                      {t("addNewInventory")}
                    </span>
                  </div>
                )}
                {isInvoice && (
                  <div className="absolute mt-[-1em] lg:hidden md:hidden">
                    <span
                      onClick={handleAdd}
                      className="flex items-center text-[#044E97] text-[10px] font-medium cursor-pointer border border-[#044E97] w-[120px] py-2 px-[1em] 320:ml-[7em] 375:ml-[3em] 360:ml-[4em] rounded-md"
                    >
                      {t("addNewInventory")}
                    </span>
                  </div>
                )}
              </div>
            </SubscriptionGuard>
          )}
          <span
            onClick={handleAddItem}
            className="flex text-[#044E97] mr-[1em] font-medium cursor-pointer items-center"
          >
            <div className="mr-3">
              <AddIcon />
            </div>
            Add new line
          </span>
        </div>
      </div>
    </>
  );
};

export { LineItemTable };
